import React, { useEffect, useState } from 'react';
import './App.css';

import {
  Header,
  Footer,
  Hero,
  Specialisations,
  Process,
  BlogSection,
  ContactSection,
  AboutProfessional,
  WhyMe,
  ForWhom,
  BlogPage,
  BlogArticlePage,
} from './components';

import { SupportedLocale } from './lib/content';

type Route = 'home' | 'about' | 'blog' | 'article';

const BASE_TITLES = {
  pl: 'Pieńkowski – Kancelaria Adwokacka',
  en: 'Pieńkowski – Law Office',
  es: 'Pieńkowski – Despacho de Abogados',
} as const;

const ROUTE_TITLES = {
  home: {
    pl: 'Strona główna',
    en: 'Home',
    es: 'Inicio',
  },
  about: {
    pl: 'O nas',
    en: 'About us',
    es: 'Sobre nosotros',
  },
  blog: {
    pl: 'Blog',
    en: 'Blog',
    es: 'Blog',
  },
  article: {
    pl: 'Artykuł',
    en: 'Article',
    es: 'Artículo',
  },
} as const;


function App() {
  /* ===========================
     LOCALE
     =========================== */
  const [locale, setLocale] = useState<SupportedLocale>(() => {
    try {
      const stored = window.localStorage.getItem('locale') as SupportedLocale | null;
      if (stored === 'pl' || stored === 'en' || stored === 'es') return stored;
    } catch {}
    return 'pl';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('locale', locale);
    } catch {}
  }, [locale]);

 /* ===========================
     ROUTE + ARTICLE
     =========================== */
  const [route, setRoute] = useState<Route>(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/blog') return 'blog';
    if (path.startsWith('/blog/')) return 'article';

    // "O nas" (PL) + EN/ES
    if (path === '/o-mnie') {
      // opcjonalnie: przekieruj stary adres na nowy
      window.history.replaceState({}, '', '/o-nas');
      return 'about';
    }
    if (path === '/o-nas' || path === '/about-me' || path === '/sobre-mi') return 'about';

    return 'home';
  });

  const [articleSlug, setArticleSlug] = useState<string>(() => {
    const path = window.location.pathname.replace(/\/+$/, '');
    if (path.startsWith('/blog/')) return path.replace('/blog/', '').replace('.html', '');
    return '';
  });

  /* ===========================
     ACTIVE SECTION
     =========================== */
  const [activeSection, setActiveSection] = useState<string>('hero');

  useEffect(() => {
    if (route !== 'home') {
      setActiveSection('');
      return;
    }

    const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));
    const marker = window.innerHeight * 0.35;

    const onScroll = () => {
      let current = sections[0]?.id || 'hero';
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top - marker <= 0) current = section.id;
        else break;
      }
      setActiveSection(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [route]);

  /* ===========================
     POLITYKA PRYWATNOŚCI – MODAL
     =========================== */
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // ESC zamyka modal
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPrivacyOpen(false);
    };
    if (privacyOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [privacyOpen]);

  // event z formularza
  useEffect(() => {
    const handler = () => setPrivacyOpen(true);
    window.addEventListener('legal:privacy', handler);
    return () => window.removeEventListener('legal:privacy', handler);
  }, []);

  /* ===========================
     POPSTATE
     =========================== */
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';

      if (path === '/blog') {
        setRoute('blog');
        setArticleSlug('');
      } else if (path.startsWith('/blog/')) {
        setRoute('article');
        setArticleSlug(path.replace('/blog/', '').replace('.html', ''));
      } else if (path === '/o-mnie' || path === '/about-me' || path === '/sobre-mi') {
        setRoute('about');
        setArticleSlug('');
      } else {
        setRoute('home');
        setArticleSlug('');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /* ===========================
     SET LOCALE FROM URL
     =========================== */
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/about-me') setLocale('en');
    else if (path === '/sobre-mi') setLocale('es');
    else if (path === '/o-mnie') setLocale('pl');
  }, [route]);

  /* ===========================
     DOCUMENT TITLE (SEO)
     =========================== */
  useEffect(() => {
    const base = BASE_TITLES[locale];
    const routeTitle = ROUTE_TITLES[route]?.[locale];

    // home → tylko nazwa kancelarii
    if (route === 'home') {
      document.title = base;
      return;
    }

    // pozostałe podstrony → "O nas | Nazwa kancelarii"
    document.title = `${routeTitle} | ${base}`;
  }, [locale, route]);

  /* ===========================
     REVEAL
     =========================== */
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    elements.forEach((el) => observer.observe(el));

    requestAnimationFrame(() => {
      elements.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.9) el.classList.add('is-visible');
      });
    });

    return () => observer.disconnect();
  }, [route, locale]);

  /* ===========================
     HASH SCROLL
     =========================== */
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.replace('#', '');
    if (!id) return;

    const t1 = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);

    const t2 = window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [route, locale]);

  /* ===========================
     RENDER
     =========================== */
  return (
    <div className="app-shell">
      <Header
        locale={locale}
        onLocaleChange={setLocale}
        activeSection={activeSection}
        isBlogPage={route === 'blog' || route === 'article'}
      />

      <main id="main">
        {route === 'home' && (
          <>
            <Hero locale={locale} />
            <Specialisations locale={locale} />
            <Process locale={locale} />
            <BlogSection locale={locale} />
            <ContactSection locale={locale} />
          </>
        )}

        {route === 'about' && (
          <>
            <AboutProfessional locale={locale} />
            <WhyMe locale={locale} />
            <ForWhom locale={locale} />
          </>
        )}

        {route === 'blog' && <BlogPage locale={locale} />}

        {route === 'article' && (
          <BlogArticlePage locale={locale} slug={articleSlug || 'paragraf-zwierzoochronny'} />
        )}
      </main>

      <Footer
        locale={locale}
        onOpenPrivacy={() => setPrivacyOpen(true)}
      />

      {/* MODAL – POLITYKA PRYWATNOŚCI */}
      {privacyOpen && (
        <div
          className="legal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Polityka prywatności"
          onClick={() => setPrivacyOpen(false)}
        >
          <div className="legal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="legal-modal__header">
              <h2 className="legal-modal__title">
                {locale === 'pl'
                  ? 'Polityka prywatności i cookies'
                  : locale === 'en'
                  ? 'Privacy policy and cookies'
                  : 'Política de privacidad y cookies'}
              </h2>
              <button
                className="legal-modal__close"
                onClick={() => setPrivacyOpen(false)}
                aria-label="Zamknij"
              >
                ✕
              </button>
            </div>

           <div className="legal-modal__body">
  {locale === 'pl' && (
    <>
      <h3>1. Administrator danych osobowych</h3>
      <p>Administratorem danych osobowych jest:</p>
      <p>
        <strong>Piotr Pieńkowski – Kancelaria Adwokacka</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Warszawa
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
      </p>
      <p>
        Administrator przetwarza dane osobowe użytkowników strony internetowej prowadzonej pod adresem
        pienkowskilegal.pl zgodnie z Rozporządzeniem Parlamentu Europejskiego i Rady Unii Europejskiej
        2016/679 z dnia 27 kwietnia 2016 roku w sprawie ochrony osób fizycznych w związku z przetwarzaniem
        danych osobowych i w sprawie swobodnego przepływu takich danych oraz uchylenia dyrektywy 95/46/WE
        (dalej: „RODO”).
      </p>
      <p>
        W sprawach związanych z ochroną danych osobowych można skontaktować się z Administratorem za
        pośrednictwem poczty elektronicznej lub listownie na wskazane wyżej adresy.
      </p>

      <h3>2. Zakres przetwarzanych danych</h3>
      <p>W zależności od sposobu korzystania ze strony, Administrator może przetwarzać następujące dane osobowe:</p>
      <ul>
        <li>imię i nazwisko,</li>
        <li>adres e-mail,</li>
        <li>numer telefonu,</li>
        <li>treść wiadomości przesłanej za pomocą formularza kontaktowego lub poczty e-mail,</li>
        <li>adres IP oraz dane techniczne związane z korzystaniem ze strony (np. typ przeglądarki).</li>
      </ul>
      <p>Podanie danych jest dobrowolne, lecz niezbędne do udzielenia odpowiedzi na zapytanie lub nawiązania kontaktu.</p>

      <h3>3. Cele i podstawy prawne przetwarzania danych</h3>
      <p>Dane osobowe są przetwarzane w następujących celach:</p>
      <ol>
        <li>
          <strong>Udzielenie odpowiedzi na zapytanie oraz prowadzenie korespondencji</strong>
          <br />
          Podstawa prawna:
          <ul>
            <li>art. 6 ust. 1 lit. b RODO – działania na żądanie osoby, której dane dotyczą,</li>
            <li>art. 6 ust. 1 lit. f RODO – prawnie uzasadniony interes Administratora polegający na prowadzeniu korespondencji,</li>
          </ul>
        </li>
        <li>
          <strong>Zapewnienie prawidłowego funkcjonowania strony internetowej oraz jej bezpieczeństwa</strong>
          <br />
          Podstawa prawna:
          <ul>
            <li>art. 6 ust. 1 lit. f RODO – prawnie uzasadniony interes Administratora,</li>
          </ul>
        </li>
        <li>
          <strong>Wypełnienie obowiązków prawnych związanych z wykonywaniem zawodu adwokata</strong>
          <br />
          Podstawa prawna:
          <ul>
            <li>art. 6 ust. 1 lit. c RODO – obowiązek prawny ciążący na Administratorze.</li>
          </ul>
        </li>
      </ol>

      <h3>4. Odbiorcy danych</h3>
      <p>Dane osobowe mogą być przekazywane:</p>
      <ul>
        <li>podmiotom świadczącym na rzecz Administratora usługi informatyczne lub hostingowe,</li>
        <li>
          podmiotom świadczącym na rzecz Administratora usługi księgowe lub doradcze – wyłącznie w zakresie
          niezbędnym do prawidłowego świadczenia ww. usług,
        </li>
        <li>
          sądom, organom administracji publicznej oraz innym uczestnikom postępowań – jeżeli jest to konieczne
          w związku ze świadczeniem pomocy prawnej,
        </li>
        <li>oraz w przypadkach wynikających z przepisów prawa powszechnie obowiązującego.</li>
      </ul>
      <p>Dane nie są przekazywane do państw trzecich poza Europejski Obszar Gospodarczy.</p>

      <h3>5. Okres przechowywania danych</h3>
      <ul>
        <li>
          dane przekazane w związku z kontaktem – przez czas niezbędny do obsługi zapytania, a następnie przez
          okres przedawnienia ewentualnych roszczeń,
        </li>
        <li>
          dane przetwarzane na podstawie obowiązków prawnych – przez okres wymagany przepisami prawa powszechnie obowiązującego.
        </li>
      </ul>

      <h3>6. Prawa osób, których dane dotyczą</h3>
      <p>Każdej osobie, której dane są przetwarzane, przysługuje prawo do:</p>
      <ul>
        <li>dostępu do danych oraz otrzymania ich kopii,</li>
        <li>sprostowania danych,</li>
        <li>usunięcia danych (w przypadkach przewidzianych prawem),</li>
        <li>ograniczenia przetwarzania danych,</li>
        <li>wniesienia sprzeciwu wobec przetwarzania danych,</li>
        <li>przenoszenia danych (jeżeli podstawą przetwarzania jest zgoda lub umowa),</li>
        <li>wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.</li>
      </ul>

      <h3>7. Pliki cookies</h3>
      <p>
        Strona internetowa wykorzystuje wyłącznie pliki cookies o charakterze technicznym, niezbędne do jej
        prawidłowego działania, w tym do zapamiętania wybranych ustawień użytkownika (np. wersji językowej strony).
      </p>
      <p>Strona nie wykorzystuje cookies analitycznych ani marketingowych oraz nie prowadzi profilowania użytkowników.</p>
      <p>Użytkownik może w każdej chwili zmienić ustawienia dotyczące cookies w swojej przeglądarce internetowej.</p>

      <h3>8. Zautomatyzowane podejmowanie decyzji</h3>
      <p>Administrator nie podejmuje decyzji w sposób zautomatyzowany, w tym w oparciu o profilowanie.</p>

      <h3>9. Zmiany Polityki prywatności</h3>
      <p>
        Administrator zastrzega sobie prawo do wprowadzania zmian w niniejszej Polityce prywatności w przypadku
        zmian przepisów prawa, technologii lub sposobu funkcjonowania strony. Aktualna wersja Polityki jest
        zawsze dostępna na stronie internetowej.
      </p>
    </>
  )}

  {locale === 'en' && (
    <>
      <h3>1. Personal data controller</h3>
      <p>The controller of your personal data is:</p>
      <p>
        <strong>Piotr Pieńkowski – Law Office</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Warsaw, Poland
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
      </p>
      <p>
        The controller processes personal data of users of the website available at pienkowskilegal.pl in
        accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council of 27 April 2016
        (GDPR).
      </p>
      <p>
        For matters related to personal data protection, you can contact the controller via e-mail or by post
        using the addresses indicated above.
      </p>

      <h3>2. Scope of processed data</h3>
      <p>Depending on how you use the website, the controller may process the following personal data:</p>
      <ul>
        <li>name and surname,</li>
        <li>e-mail address,</li>
        <li>phone number,</li>
        <li>the content of a message sent via the contact form or by e-mail,</li>
        <li>IP address and technical data related to the use of the website (e.g., browser type).</li>
      </ul>
      <p>Providing data is voluntary, but necessary to respond to an enquiry or to establish contact.</p>

      <h3>3. Purposes and legal bases for processing</h3>
      <p>Personal data are processed for the following purposes:</p>
      <ol>
        <li>
          <strong>Responding to enquiries and conducting correspondence</strong>
          <br />
          Legal basis:
          <ul>
            <li>Art. 6(1)(b) GDPR – taking steps at the data subject’s request prior to entering into a contract,</li>
            <li>Art. 6(1)(f) GDPR – the controller’s legitimate interest in handling correspondence,</li>
          </ul>
        </li>
        <li>
          <strong>Ensuring proper operation and security of the website</strong>
          <br />
          Legal basis:
          <ul>
            <li>Art. 6(1)(f) GDPR – the controller’s legitimate interest,</li>
          </ul>
        </li>
        <li>
          <strong>Compliance with legal obligations related to practising as an адвокат (attorney-at-law)</strong>
          <br />
          Legal basis:
          <ul>
            <li>Art. 6(1)(c) GDPR – a legal obligation of the controller.</li>
          </ul>
        </li>
      </ol>

      <h3>4. Data recipients</h3>
      <p>Personal data may be disclosed to:</p>
      <ul>
        <li>entities providing IT or hosting services to the controller,</li>
        <li>entities providing accounting or advisory services – only to the extent necessary to provide such services,</li>
        <li>
          courts, public administration authorities and other parties to proceedings – if necessary in connection with the provision of legal assistance,
        </li>
        <li>and where required by applicable law.</li>
      </ul>
      <p>Your data are not transferred to third countries outside the European Economic Area.</p>

      <h3>5. Data retention period</h3>
      <ul>
        <li>
          data provided in connection with contact – for the time necessary to handle the enquiry, and then for the limitation period of potential claims,
        </li>
        <li>
          data processed to comply with legal obligations – for the period required by generally applicable law.
        </li>
      </ul>

      <h3>6. Rights of data subjects</h3>
      <p>Each person whose data are processed has the right to:</p>
      <ul>
        <li>access their data and receive a copy,</li>
        <li>rectify data,</li>
        <li>erase data (in cases provided by law),</li>
        <li>restrict processing,</li>
        <li>object to processing,</li>
        <li>data portability (where processing is based on consent or a contract),</li>
        <li>lodge a complaint with the competent data protection authority.</li>
      </ul>

      <h3>7. Cookies</h3>
      <p>
        The website uses only technical cookies necessary for its proper operation, including remembering selected user settings
        (e.g., the language version of the website).
      </p>
      <p>The website does not use analytics or marketing cookies and does not profile users.</p>
      <p>You can change your cookie settings at any time in your web browser.</p>

      <h3>8. Automated decision-making</h3>
      <p>The controller does not make decisions in an automated manner, including profiling.</p>

      <h3>9. Changes to this policy</h3>
      <p>
        The controller reserves the right to amend this Privacy Policy in the event of changes in law, technology or the way the website operates.
        The current version of the policy is always available on the website.
      </p>
    </>
  )}

  {locale === 'es' && (
    <>
      <h3>1. Responsable del tratamiento</h3>
      <p>El responsable del tratamiento de los datos personales es:</p>
      <p>
        <strong>Piotr Pieńkowski – Despacho de Abogados</strong>
        <br />
        ul. Bracka 20/7a, 00-028 Varsovia, Polonia
        <br />
        e-mail: biuro@pienkowskilegal.pl
        <br />
        tel.: +48 889 054 555
      </p>
      <p>
        El responsable trata los datos personales de los usuarios del sitio web disponible en pienkowskilegal.pl de conformidad con
        el Reglamento (UE) 2016/679 del Parlamento Europeo y del Consejo, de 27 de abril de 2016 (RGPD).
      </p>
      <p>
        Para asuntos relacionados con la protección de datos personales, puede ponerse en contacto con el responsable por correo electrónico
        o por vía postal utilizando las direcciones indicadas arriba.
      </p>

      <h3>2. Alcance de los datos tratados</h3>
      <p>Dependiendo de cómo utilice el sitio web, el responsable puede tratar los siguientes datos personales:</p>
      <ul>
        <li>nombre y apellidos,</li>
        <li>dirección de correo electrónico,</li>
        <li>número de teléfono,</li>
        <li>contenido del mensaje enviado mediante el formulario de contacto o por correo electrónico,</li>
        <li>dirección IP y datos técnicos relacionados con el uso del sitio (por ejemplo, tipo de navegador).</li>
      </ul>
      <p>La aportación de datos es voluntaria, pero necesaria para responder a una consulta o establecer contacto.</p>

      <h3>3. Finalidades y bases jurídicas del tratamiento</h3>
      <p>Los datos personales se tratan con las siguientes finalidades:</p>
      <ol>
        <li>
          <strong>Responder a consultas y mantener la correspondencia</strong>
          <br />
          Base jurídica:
          <ul>
            <li>art. 6.1(b) RGPD – medidas a solicitud del interesado antes de celebrar un contrato,</li>
            <li>art. 6.1(f) RGPD – interés legítimo del responsable consistente en gestionar la correspondencia,</li>
          </ul>
        </li>
        <li>
          <strong>Garantizar el correcto funcionamiento y la seguridad del sitio web</strong>
          <br />
          Base jurídica:
          <ul>
            <li>art. 6.1(f) RGPD – interés legítimo del responsable,</li>
          </ul>
        </li>
        <li>
          <strong>Cumplir obligaciones legales relacionadas con el ejercicio de la profesión de abogado</strong>
          <br />
          Base jurídica:
          <ul>
            <li>art. 6.1(c) RGPD – obligación legal aplicable al responsable.</li>
          </ul>
        </li>
      </ol>

      <h3>4. Destinatarios de los datos</h3>
      <p>Los datos personales pueden comunicarse a:</p>
      <ul>
        <li>entidades que presten al responsable servicios informáticos o de alojamiento (hosting),</li>
        <li>
          entidades que presten servicios contables o de asesoramiento – únicamente en la medida necesaria para la correcta prestación de dichos servicios,
        </li>
        <li>
          tribunales, autoridades administrativas y otros participantes en procedimientos – cuando sea necesario en relación con la prestación de asistencia jurídica,
        </li>
        <li>y en los casos previstos por la normativa aplicable.</li>
      </ul>
      <p>Los datos no se transfieren a terceros países fuera del Espacio Económico Europeo.</p>

      <h3>5. Plazo de conservación</h3>
      <ul>
        <li>
          datos proporcionados en relación con el contacto – durante el tiempo necesario para gestionar la consulta y, posteriormente,
          durante el plazo de prescripción de posibles reclamaciones,
        </li>
        <li>
          datos tratados para cumplir obligaciones legales – durante el período exigido por la normativa de aplicación general.
        </li>
      </ul>

      <h3>6. Derechos de las personas interesadas</h3>
      <p>La persona cuyos datos se tratan tiene derecho a:</p>
      <ul>
        <li>acceder a sus datos y obtener una copia,</li>
        <li>rectificar los datos,</li>
        <li>suprimir los datos (en los casos previstos por la ley),</li>
        <li>limitar el tratamiento,</li>
        <li>oponerse al tratamiento,</li>
        <li>portabilidad de los datos (cuando el tratamiento se base en el consentimiento o en un contrato),</li>
        <li>presentar una reclamación ante la autoridad de protección de datos competente.</li>
      </ul>

      <h3>7. Cookies</h3>
      <p>
        El sitio web utiliza únicamente cookies técnicas necesarias para su correcto funcionamiento, incluido recordar ciertos ajustes del usuario
        (por ejemplo, la versión lingüística del sitio).
      </p>
      <p>El sitio no utiliza cookies analíticas ni de marketing y no realiza perfiles de usuarios.</p>
      <p>Puede modificar la configuración de cookies en cualquier momento en su navegador.</p>

      <h3>8. Decisiones automatizadas</h3>
      <p>El responsable no adopta decisiones de forma automatizada, incluida la elaboración de perfiles.</p>

      <h3>9. Cambios en la política</h3>
      <p>
        El responsable se reserva el derecho de introducir cambios en esta Política de privacidad en caso de cambios en la legislación,
        la tecnología o el funcionamiento del sitio web. La versión vigente de la política está siempre disponible en el sitio.
      </p>
    </>
  )}
</div>

          </div>
        </div>
      )}
    </div>
  );
}

export default App;
